import { z } from "zod";
import {
  ReservationServiceClientDtoSchema,
  ReservationServiceClientFromDto,
  ReservationServiceClientSchema,
  ReservationServiceClientToDto,
} from "./reservation-service-client";
import { apiListSchema } from "./list";

//? DTO
export const CreateReservationServiceDtoSchema = z.object({
  ReservationId: z.number(),
  ServiceId: z.number(),
  Day: z.number(),
  Hour: z.string(),
  State: z.string().nullish(),
  Order: z.number().nullish(),
  Notes: z.string().nullish(),
  ClientNotes: z.string().nullish(),
  ServiceProviderId: z.number().nullish(),
  Price: z.number().nullish(),
  ReservationServiceClients: z
      .array(ReservationServiceClientDtoSchema)
      .nullish(),
});
export type CreateReservationServiceDto = z.infer<
  typeof CreateReservationServiceDtoSchema
>;

export const ReservationServiceDtoSchema =
  CreateReservationServiceDtoSchema.extend({
    Id: z.number(),
  });
export type ReservationServiceDto = z.infer<typeof ReservationServiceDtoSchema>;

export const reservationServiceDtoListSchema = apiListSchema(
  ReservationServiceDtoSchema,
);
//? Model

export enum ReservationServiceState {
  NO = "NO",
  SentToProvider = "SentToProvider",
  Ongoing = "Ongoing",
  Phone = "Phone",
  PreBooking = "PreBooking",
  OK = "OK",
  Done = "Done",
  PercentDone = "PercentDone",
  NotDone = "NotDone",
  Canceled = "Canceled",
}

export const reservationServiceStateArray = Object.values(
  ReservationServiceState,
);

export const CreateReservationServiceSchema = z.object({
  reservationId: z.number(),
  serviceId: z.number(),
  day: z.number(),
  state: z.nativeEnum(ReservationServiceState).nullish(),
  startHour: z.string(),
  endHour: z.string(),
  order: z.number(),
  clientNotes: z.string().nullish(),
  notes: z.string().nullish(),
  serviceProviderId: z.number().nullish(),
  reservationClients: z.array(ReservationServiceClientSchema),
  price: z.number().nullish(),
});

export const ReservationServiceSchema = CreateReservationServiceSchema.extend({
  id: z.string().min(1),
});

export type CreateReservationService = z.infer<
  typeof CreateReservationServiceSchema
>;

export type ReservationService = z.infer<typeof ReservationServiceSchema>;

//? Mappers

export function reservationServiceFromDto(
  dto: ReservationServiceDto,
): ReservationService {
  const [startHour, endHour] = dto.Hour.split("|");
  return {
    id: dto.Id.toString(),
    reservationId: dto.ReservationId,
    serviceId: dto.ServiceId,
    day: dto.Day,
    state: dto.State ? (dto.State as ReservationServiceState) : undefined,
    endHour,
    startHour,
    order: dto.Order ?? 0,
    notes: dto.Notes,
    clientNotes: dto.ClientNotes,
    serviceProviderId: dto.ServiceProviderId,
    price: dto.Price,
    reservationClients:
      dto.ReservationServiceClients?.map(
        ReservationServiceClientFromDto,
      ) ?? [],
  };
}

export function reservationServiceToDto(
  dto: ReservationService,
): ReservationServiceDto {
  return {
    Id: Number.parseInt(dto.id),
    ...createReservationServiceToDto(dto, dto.id),
  };
}

export function createReservationServiceToDto(
  dto: CreateReservationService,
  id?: string,
): CreateReservationServiceDto {

  return {
    ReservationId: dto.reservationId,
    ServiceId: dto.serviceId,
    Day: dto.day,
    State: dto.state?.toString(),
    Hour: `${dto.startHour}|${dto.endHour}`,
    Order: dto.order,
    ClientNotes: dto.clientNotes,
    Notes: dto.notes,
    ServiceProviderId: dto.serviceProviderId,
    Price: dto.price,
    ReservationServiceClients: id
      ? dto.reservationClients.map(ReservationServiceClientToDto)
      : undefined,
  };
}
